import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useEffect } from "react";

import { Building2, HardHat, Wrench, Ruler } from "lucide-react";

const services = [
  {
    title: "Architecture",
    icon: Building2,
    desc: "Smart, sustainable buildings that shape modern skylines.",
    strategy:
      "Our architectural design philosophy merges functionality with visionary aesthetics. We focus on client-specific designs that prioritize usability, environmental integration, and cultural relevance.",
    sustainability:
      "We implement green building standards, passive design techniques, and resource-efficient materials to reduce carbon footprints and improve energy efficiency throughout a building’s lifecycle.",
    image: "./images/services/architecture.jpg",
  },
  {
    title: "Structural Engineering",
    icon: HardHat,
    desc: "Reliable, resilient structural systems engineered to last.",
    strategy:
      "We utilize state-of-the-art modeling tools and performance-based engineering methods to ensure the structural integrity and longevity of every project.",
    sustainability:
      "Our approach embraces lifecycle durability, material optimization, and minimal waste production to ensure both cost-efficiency and environmental responsibility.",
    image: "./images/services/structural.jpg",
  },
  {
    title: "Civil Engineering",
    icon: Wrench,
    desc: "Enabling infrastructure that connects communities.",
    strategy:
      "We address real-world challenges through infrastructure solutions that enhance mobility, water access, sanitation, and urban development with resilience at their core.",
    sustainability:
      "Our designs support low-impact development, stormwater management, and sustainable urban drainage systems (SUDS) to foster long-term environmental resilience.",
    image: "./images/services/civil.jpg",
  },
  {
    title: "Quantity Surveying",
    icon: Ruler,
    desc: "Cost planning and budget control from start to finish.",
    strategy:
      "We apply precise cost management, value engineering, and transparent procurement strategies to ensure optimal financial control throughout the project lifecycle.",
    sustainability:
      "We promote cost efficiency without compromising on sustainability, prioritizing locally sourced, eco-friendly materials and lifecycle cost assessments.",
    image: "./images/services/quantity.jpg",
  },
];

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
    <Navbar/>
    <section id="services" className="mx-auto max-w-7xl px-6 py-28 bg-primary text-text-light">
      <motion.h2
        className="mb-16 text-center font-heading text-5xl font-bold text-accent"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
      >
        Our Services in Detail
      </motion.h2>

      <div className="grid grid-cols-1 gap-14 lg:grid-cols-2">
        {services.map(({ title, icon: Icon, desc, strategy, sustainability, image }, index) => (
          <motion.div
            key={title}
            className="relative rounded-3xl overflow-hidden shadow-lg group"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.2 }}
            viewport={{ once: true }}
          >
            <div className="relative h-[300px] overflow-hidden">
              <img
                src={image}
                alt={title}
                className="absolute inset-0 h-full w-full object-cover scale-100 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent z-10" />
              <div className="absolute bottom-4 left-4 z-20 flex items-center space-x-4">
                <div className="bg-white/10 backdrop-blur-md p-3 rounded-full border border-accent">
                  <Icon className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">{title}</h3>
                  <p className="text-sm text-gray-300">{desc}</p>
                </div>
              </div>
            </div>

            <div className="bg-primary-light p-8 text-base leading-relaxed space-y-6">
              <div>
                <h4 className="text-lg font-bold text-accent mb-2">Our Strategy</h4>
                <p className="text-text-light/90">{strategy}</p>
              </div>
              <div>
                <h4 className="text-lg font-bold text-accent mb-2">Sustainability Focus</h4>
                <p className="text-text-light/90">{sustainability}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
    <Footer/>
    </>
  );
}
